import * as React from 'react';
import './Admin.less';
import { Form, Input, Button, Checkbox, Divider, message, Select, Tooltip, Spin, Row, Col } from 'antd';
import { FormComponentProps } from 'antd/lib/form/Form';
import { withRouter, Link, Redirect } from 'react-router-dom';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import Routes from 'src/core/Routes';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';

import AddStatementDTO from 'src/models/statement/AddStatementDTO';
import UpdateStatementDTO from 'src/models/statement/UpdateStatementDTO';
import TranslationDTO from 'src/models/generic/TranslationDTO';
import KeyValueListDTO from 'src/models/generic/KeyValueListDTO';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import StatementApiController from 'src/api/StatementApiController';
import GradeApiController from 'src/api/GradeApiController';
import CultureApiController from 'src/api/CultureApiController';
import TextArea from 'antd/lib/input/TextArea';
import Languages, { Language } from 'src/constants/Languages';
import { Grades, Grade } from 'src/constants/Grades';


interface RouteParams {
    id: string;
}

interface AddEditStatementProps extends PageProps<RouteParams>, LocalizeContextProps { }

interface AddEditStatementState {
    loading: boolean;
    isSubmitting: boolean;
    currentStatement: UpdateStatementDTO;
    allGrades: Grade[];
    allCultures: Language[];
    selectedCultureId: number;
    redirect: boolean;
}

interface StatementFormProps {
    currentStatement: UpdateStatementDTO;
    allGrades: Grade[];
    allCultures: Language[];
    selectedCultureId: number;
    translate: (id: any) => any;
    onChange: (changedFields: any[]) => void;
}

// Form
let StatementForm = Form.create({
    onFieldsChange(props: StatementFormProps, changedFields: any[]) {
        props.onChange(changedFields);
    }
})(class extends React.Component<StatementFormProps & FormComponentProps> {
    constructor(props: StatementFormProps & FormComponentProps) {
        super(props);
    }

    private generateOptionsForSelect(keyValues: KeyValueDTO<string, number>[]): JSX.Element[] {
        return keyValues.map(keyValue => <Select.Option value={keyValue.value} key={keyValue.key} title={keyValue.key}>{keyValue.key}</Select.Option>);
    }

    private generateLanguageOptions() {
        // Only available languages
        return Languages.All.filter(x => x.isAvailable)
            .map(x => <Select.Option key={`cultureIds${x.id}`} value={x.id}>{x.name} ({x.code})</Select.Option>);
    }

    private renderTranslations(): JSX.Element[] {
        if (this.props.currentStatement) {
            let translations = this.props.currentStatement.translations.filter(t => this.props.allCultures.find(c => c.id === t.cultureId));

            return translations.map((translation: TranslationDTO, index: number) => {
                let id = "translations[" + index + "].text";
                let culture = this.props.allCultures.find(culture => culture.id === translation.cultureId).code.toLowerCase();
                let translationId = "admin.addEditStatement." + culture;

                return this.renderStandardFormItem(
                    translationId,
                    id,
                    translation.text,
                    <TextArea rows={4} autoComplete="off" />,
                    this.props.selectedCultureId === translation.cultureId
                );
            });
        }
    }

    private renderStandardFormItem(translationId: string, id: string, initialValue, inputType, visible: boolean = true, required: boolean = true) {
        let translatedLabel = this.props.translate(translationId);

        if (visible) {
            return (
                <Form.Item label={translatedLabel}>
                    {this.props.form.getFieldDecorator(id, {
                        initialValue: initialValue,
                        rules: [{ required: required, message: this.props.translate('admin.common.requiredRule') }]
                    })(inputType)}
                </Form.Item>
            );
        }
    }

    render() {
        return (
            <Row>
                <Col span={12}>
                    <Form className="admin-form">
                        {this.renderStandardFormItem(
                            "admin.addEditStatement.formItem1",
                            "gradeIds",
                            this.props.currentStatement.gradeIds,
                            <Select optionFilterProp="title" mode="multiple">
                                {Grades.All.map(x => <Select.Option key={`grade${x.id}`} value={x.id}>{x.shortName}</Select.Option>)
                                }</Select>
                        )}

                        {this.renderStandardFormItem(
                            "admin.addEditStatement.formItem2",
                            "selectedCultureId",
                            this.props.selectedCultureId,
                            <Select>{this.generateLanguageOptions()}</Select>
                        )}

                        {this.renderTranslations()}
                    </Form>
                </Col>
            </Row>
        );
    }
});

class AddEditStatement extends React.Component<AddEditStatementProps, AddEditStatementState> {
    private formRef: React.Component<FormComponentProps>;

    constructor(props: AddEditStatementProps) {
        super(props);
        bindAllOfThis(this, AddEditStatement.prototype);

        let statementId = parseInt(this.props.match.params.id, 10);

        let currentStatement: UpdateStatementDTO = {
            statementId: statementId,
            isActive: true,
            gradeIds: [],
            translations: []
        };

        this.state = {
            loading: true,
            isSubmitting: false,
            redirect: false,
            allGrades: [],
            allCultures: [],
            selectedCultureId: 0,
            currentStatement: currentStatement
        };
    }

    componentWillMount(): void {
        let grades = Grades.All;
        let cultures = Languages.All.filter(x => x.isAvailable);

        this.setState({
            allGrades: grades,
            allCultures: cultures,
            selectedCultureId: cultures[0].id
        }, this.fillForm);
    }

    private statementsAreCompleted(): boolean {
        let translations = this.state.currentStatement.translations;
        let emptyTranslations = translations.filter(translation =>
            translation.text === null || translation.text === ""
        );

        return emptyTranslations.length === 0;
    }

    private fillForm(): void {
        if (this.state.currentStatement.statementId) {
            StatementApiController.getStatement(this.state.currentStatement.statementId).then((result) => {
                this.setState({
                    currentStatement: result.data,
                    loading: false
                });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditStatement.messageRetrieveError'));
            });
        } else {
            let translations = this.state.allCultures.map(
                (culture: Language): TranslationDTO => {
                    let translation: TranslationDTO = {
                        cultureId: culture.id,
                        text: null
                    };

                    return translation;
                }
            );

            let statement = {
                translations: translations
            } as UpdateStatementDTO;

            this.setState({
                currentStatement: { ...this.state.currentStatement, ...statement },
                loading: false
            });
        }
    }

    private handleInputChange(changedFields: any[]): void {
        let newState = {};
        for (var key in changedFields) {
            if (changedFields.hasOwnProperty(key)) {
                let field: any = changedFields[key];

                // If key is the nested property
                if (key === 'translations') {
                    let fieldKey = '';
                    for (var fieldName in field) {
                        if (field.hasOwnProperty(fieldName)) {
                            fieldKey = fieldName;
                            break;
                        }
                    }

                    newState[key] = [...this.state.currentStatement[key]];
                    newState[key][fieldKey].text = field[fieldKey].text.value;
                } else if (key === 'selectedCultureId') {
                    this.setState({ selectedCultureId: field.value });
                    return;
                } else {
                    newState[key] = field.value;
                }
            }
        }
        this.setState({
            currentStatement: { ...this.state.currentStatement, ...newState }
        });

    }

    private addStatement(): void {
        let addStatementDTO: AddStatementDTO = {
            isActive: true,
            gradeIds: this.state.currentStatement.gradeIds,
            translations: this.state.currentStatement.translations
        };

        StatementApiController.addStatement(addStatementDTO)
            .then(result => {
                message.success(this.props.translate('admin.addEditStatement.messageAdd'));
                this.setState({ redirect: true, isSubmitting: false });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditStatement.messageAddError'));
                this.setState({ isSubmitting: false });
            });
    }

    private updateStatement(): void {
        StatementApiController.updateStatement(this.state.currentStatement)
            .then(result => {
                message.success(this.props.translate('admin.addEditStatement.messageUpdate'));
                this.setState({ redirect: true, isSubmitting: false });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditStatement.messageUpdateError'));
                this.setState({ isSubmitting: false });
            });
    }

    private handleSubmit(e): void {
        e.preventDefault();

        this.formRef.props.form.validateFields((error: any, values: any) => {
            if (!error) {
                this.setState({ isSubmitting: true });
                this.state.currentStatement.statementId ? this.updateStatement() : this.addStatement();
            }
        });
    }

    private handleCancel(e): void {
        this.setState({ redirect: true });
    }

    private renderSaveButton(): JSX.Element {
        if (this.statementsAreCompleted()) {
            return (
                <Button type="primary" onClick={this.handleSubmit} loading={this.state.isSubmitting}>
                    {this.props.translate("admin.common.saveButton")}
                </Button>
            );
        }

        return (
            <Tooltip placement="bottom" title={this.props.translate('admin.addEditStatement.saveTooltip')} arrowPointAtCenter>
                <Button type="primary" disabled>
                    {this.props.translate("admin.common.saveButton")}
                </Button>
            </Tooltip>
        );
    }

    render() {
        if (this.state.redirect) {
            return <Redirect to={Routes.ADMIN_STATEMENTS} push />;
        } else if (this.state.loading) {
            return <Spin />;
        }

        let formProps = {
            currentStatement: this.state.currentStatement,
            allGrades: this.state.allGrades,
            allCultures: this.state.allCultures,
            selectedCultureId: this.state.selectedCultureId,
            translate: this.props.translate,
            onChange: this.handleInputChange
        } as StatementFormProps;

        return (
            <div>
                <h2>
                    {this.state.currentStatement.statementId ?
                        this.props.translate('admin.addEditStatement.editHeader') :
                        this.props.translate('admin.addEditStatement.addHeader')}
                </h2>

                <StatementForm wrappedComponentRef={ref => this.formRef = ref} {...formProps} />

                <div>
                    {this.renderSaveButton()}
                    <Divider type="vertical" />
                    <Button onClick={this.handleCancel}>
                        {this.props.translate("common.back")}
                    </Button>
                </div>
            </div>
        );
    }
}

export default withLocalize(AddEditStatement);



// WEBPACK FOOTER //
// ./src/pages/admin/AddEditStatement.tsx